import { useLazyGetPlaybookQuery } from '@carboncopyco/data';
import { CloseOutlineIcon, PlayIcon } from '@carboncopyco/icons';
import type { IDemo, Playbook } from '@carboncopyco/types';
import { type IGlobalTab, TabType } from '@carboncopyco/types/desktop';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { useEffect, useState } from 'react';
import { isBrowserTab } from '../../../shared/globalTabUtil';
import { useEditorPageInfo } from '../../components/EditorContainer/hooks';
import GiveADemoModalExtended from '../../components/GiveADemoModalExtended';
import PresentAndPreviewToolbar from '../../components/PresentAndPreviewToolbar';
import ResponsiveModeButton from '../../components/ResponsiveModeButton';
import { useCustomNavigate } from '../../hooks/useCustomNavigate';
import useEditorGroupTab from '../../hooks/useEditorGroupTab';
import { useFlagsFallback } from '../../hooks/useFlagsFallback';
import { useFocusAppService } from '../../hooks/useFocusAppService';
import useStartDemo from '../../hooks/useStartDemo';
import { useTabFocus } from '../../hooks/useTabFocus';
import { HOME_TAB_ID, useTabManager } from '../../services/TabManager';
import { isActiveOrAllInnerTabsLoaded } from '../../shared/utils';
import { useTabs, useUser } from '../../store/context';

const DemoPlayScreenToolbar = ({
  tab,
  isPresentMode,
  isSpeakerPannelVisible,
}: {
  tab: IGlobalTab;
  isPresentMode: boolean;
  isSpeakerPannelVisible: boolean;
}) => {
  const demo = tab.payload.demo as IDemo;
  const { showWithoutDrafts, play } = tab.payload;
  const [activePlaybook, setActivePlaybook] = useState<Playbook>();
  const [showCreatePresentation, setShowCreatePresentation] = useState(false);
  const [isCreatePresentationLoading, setIsCreatePresentationLoading] =
    useState(true);
  const { activeOrganization } = useUser();
  const { trackEvent } = useAnalytics();
  const flags = useFlagsFallback();
  const {
    closeGlobalActiveTab,
    getActiveGlobalTab,
    closeGroupTabs,
    closeGroupTabsWithException,
  } = useTabManager();
  const { setActiveGlobalTabId, setRedirectRoute } = useTabs();
  const { triggerFocus } = useFocusAppService();
  const { links } = useCustomNavigate();
  const pageInfo = useEditorPageInfo(tab);
  const { getFirstGroupTabByGroupId, updateEditorTabVisibility } =
    useEditorGroupTab();
  useTabFocus({
    tab,
    onTabIsDeactivated: () => {
      setShowCreatePresentation(false);
    },
  });
  const { onStartDemo } = useStartDemo({
    tab,
    setIsCreatePresentationLoading,
    setShowCreatePresentation,
  });

  const isInnerViewLoaded = isActiveOrAllInnerTabsLoaded(tab);
  const activeTab = getActiveGlobalTab();

  const [getPlaybook] = useLazyGetPlaybookQuery();

  const isActiveTab = activeTab?.id === tab.id;
  const activeInnerTabId = isActiveTab ? activeTab.activeInnerTabId : undefined;

  const fetchPlaybook = async () => {
    const playbook = await getPlaybook({
      workspaceId: tab?.workspaceId || '',
      organizationId: activeOrganization.id.toString(),
      id: tab.payload.play.playbookId,
    }).unwrap();
    setActivePlaybook(playbook);
  };

  useEffect(() => {
    if (isInnerViewLoaded && isCreatePresentationLoading) {
      setIsCreatePresentationLoading(false);
    }
  }, [isInnerViewLoaded]);

  useEffect(() => {
    if (tab?.payload?.play?.playbookId) {
      fetchPlaybook();
    }
  }, [tab?.payload?.play]);

  const isCTAButtonVisible =
    !isPresentMode && (!demo?.isDeleted || tab?.group?.name);

  const onExitPreviewClick = () => {
    if (tab.payload?.isOpenedFromEditor) {
      const editorTab = tab.group && getFirstGroupTabByGroupId(tab.group.id);
      if (editorTab) {
        updateEditorTabVisibility({
          editorTab,
          show: true,
        });
        closeGroupTabsWithException(tab, isBrowserTab, editorTab);
      } else {
        closeGlobalActiveTab();
      }

      trackEvent(AnalyticsTrackEventName.DEMO_CLICK_EXIT_PREVIEW, {
        location: 'demo_preview_page',
        demoId: demo.id,
      });

      return;
    }

    if (tab.group?.closeGroupOnClose) {
      closeGroupTabs(tab);
      return;
    }

    closeGlobalActiveTab();
  };

  const isPresentationFromArchive = tab?.payload?.presentation?.isDeleted;
  const isItemArchived =
    ([TabType.PREVIEW_DEMO, TabType.PREVIEW_OVERLAY].includes(tab.type) &&
      (tab.payload?.demo as IDemo)?.isDeleted) ||
    play?.isDeleted ||
    isPresentationFromArchive;

  const isStartDemoVisible =
    (!tab?.payload?.isOpenedFromEditor ||
      tab.type === TabType.GROUP_DEMO_PREVIEW) &&
    !isItemArchived &&
    showWithoutDrafts;
  const ctaButton = isStartDemoVisible
    ? {
        title: 'Start demo',
        onClick: onStartDemo,
        icon: <PlayIcon />,
      }
    : {
        title: 'Exit preview',
        onClick: onExitPreviewClick,
        icon: <CloseOutlineIcon />,
      };
  return (
    <>
      <PresentAndPreviewToolbar
        tab={tab}
        speakerPannel={{
          isVisible: isSpeakerPannelVisible,
        }}
        additionalButtons={
          isPresentMode && flags.sandboxMobileView && activeInnerTabId ? (
            <ResponsiveModeButton activeInnerTabId={activeInnerTabId} />
          ) : undefined
        }
        ctaButton={isCTAButtonVisible ? ctaButton : undefined}
        pageInfo={pageInfo}
      />

      {showCreatePresentation && (
        <GiveADemoModalExtended
          open
          workspaceId={demo.workspaceId}
          entity={activePlaybook ?? demo}
          onPresent={() => {
            setShowCreatePresentation(false);
            setIsCreatePresentationLoading(false);
          }}
          onSave={() => {
            const redirectUrl = activePlaybook
              ? links.getPlaybookPresentationTabLink(
                  activePlaybook.id,
                  demo.workspaceId
                )
              : links.getPresentationTabLink(demo.id, demo.workspaceId);
            setActiveGlobalTabId(HOME_TAB_ID);
            setRedirectRoute(redirectUrl);
            setShowCreatePresentation(false);
            setIsCreatePresentationLoading(false);
          }}
          onClose={() => {
            triggerFocus(false);
            setShowCreatePresentation(false);
            setIsCreatePresentationLoading(false);
          }}
        />
      )}
    </>
  );
};

export default DemoPlayScreenToolbar;
